import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, CheckCircle, XCircle } from 'lucide-react';
import Tabs from '../ui/Tabs.jsx';
import FeedbacksCurrentModern from './FeedbacksCurrentModern.jsx';
import FeedbacksClosedModern from './FeedbacksClosedModern.jsx';
import FeedbacksRejectedModern from './FeedbacksRejectedModern.jsx';

const FeedbacksTabs = ({ onViewDetail, onLike, onRefresh }) => {
    const [feedbacks, setFeedbacks] = useState({ open: [], closed: [], rejected: [] });
    const [counts, setCounts] = useState({ open: 0, closed: 0, rejected: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);

    const fetchFeedbacks = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await fetch('index.php/feedbacks/getAll');
            if (!response.ok) {
                throw new Error('Failed to fetch feedbacks');
            }
            const data = await response.json();
            setFeedbacks({
                open: data.open || [],
                closed: data.closed || [],
                rejected: data.rejected || [],
            });
            setCounts(data.counts || { open: 0, closed: 0, rejected: 0 });
            setIsAdmin(data.is_admin || false);
        } catch (error) {
            console.error('Error fetching feedbacks:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFeedbacks();
    }, [fetchFeedbacks]);

    // Listen for refresh events
    useEffect(() => {
        const handleRefresh = () => {
            fetchFeedbacks();
        };

        window.addEventListener('feedbackListRefresh', handleRefresh);
        return () => {
            window.removeEventListener('feedbackListRefresh', handleRefresh);
        };
    }, [fetchFeedbacks]);

    // Expose refresh function to parent
    useEffect(() => {
        if (onRefresh) {
            onRefresh(fetchFeedbacks);
        }
    }, [onRefresh, fetchFeedbacks]);

    const handleLike = async (feedbackId) => {
        try {
            const response = await fetch('index.php/feedbacks/toggleLike', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ feedbackId }),
            });

            const result = await response.json();
            if (response.ok && result.success) {
                // Update local state optimistically
                setFeedbacks((prev) => ({
                    ...prev,
                    open: prev.open.map((f) =>
                        f.feedback_id === feedbackId
                            ? { ...f, likes: result.likes, user_has_liked: true }
                            : f
                    ),
                }));
            }
        } catch (error) {
            console.error('Error liking feedback:', error);
        }
    };

    const tabsData = [
        {
            id: 'current',
            name: 'Aktuell',
            icon: MessageSquare,
            count: counts.open,
            content: (
                <FeedbacksCurrentModern
                    feedbacks={feedbacks.open}
                    isLoading={isLoading}
                    onViewDetail={onViewDetail}
                    onLike={handleLike}
                    isAdmin={isAdmin}
                />
            ),
        },
        {
            id: 'closed',
            name: 'Abgeschlossen',
            icon: CheckCircle,
            count: counts.closed,
            content: (
                <FeedbacksClosedModern
                    feedbacks={feedbacks.closed}
                    isLoading={isLoading}
                    onViewDetail={onViewDetail}
                />
            ),
        },
        {
            id: 'rejected',
            name: 'Abgelehnt',
            icon: XCircle,
            count: counts.rejected,
            content: (
                <FeedbacksRejectedModern
                    feedbacks={feedbacks.rejected}
                    isLoading={isLoading}
                    onViewDetail={onViewDetail}
                />
            ),
        },
    ];

    return <Tabs tabs={tabsData} />;
};

export default FeedbacksTabs;
